// MatchWhyPhrasingService.js
// ---------------------------------------------------------------------------
// Turns a per-match fact list (from openDotaPickInsight.js, already rendered
// into full sentences by formatFacts()) into a short 1-2 sentence "why"
// blurb, using a local Ollama model (Mac Mini, over Tailscale).
//
// Same guardrail principle as the old aggregate rationale (see git history):
// llama3.2:3b does not reliably hold to negative constraints in free text —
// tested empirically, it invents facts even when told not to. So the model
// NEVER writes a fact. Each fact sentence is fully pre-built in code by the
// caller (numbers, times, outcomes all real). The model's only job here is a
// closed selection: pick which 1-2 of the given fact-sentence INDEXES matter
// most, and pick a connective word from a fixed list to join them. Both
// outputs are validated against their closed sets before use; anything
// invalid falls back to a deterministic default (first facts, "and").
// ---------------------------------------------------------------------------

const OLLAMA_URL = process.env.REACT_APP_OLLAMA_URL || 'http://100.76.43.66:11434';
const MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'llama3.2:3b';
const TIMEOUT_MS = 30000;

const CONNECTIVES = ['and', 'which set up', 'despite', 'followed by'];

function selectionPrompt(facts) {
  const numbered = facts.map((f, i) => `${i + 1}. ${f}`).join('\n');
  return (
    `Below are ${facts.length} true facts about one hero's performance in a game, numbered 1-${facts.length}. ` +
    `Reply with exactly two things, nothing else:\n` +
    `Line 1: the numbers (1-${facts.length}) of the 1 or 2 most notable facts, most notable first, comma-separated (e.g. "2,1" or "1").\n` +
    `Line 2: one connective word from this list that best links them: ${CONNECTIVES.join(', ')}.\n\n` +
    `Facts:\n${numbered}\n\n` +
    `Line 1:\nLine 2:`
  );
}

function parseSelection(raw, factCount) {
  const lines = (raw || '').trim().split('\n').map((l) => l.trim()).filter(Boolean);
  const indexLine = lines[0] || '';
  const connectiveLine = (lines[1] || '').toLowerCase();

  const indexes = indexLine
    .split(',')
    .map((s) => parseInt(s.replace(/[^0-9]/g, ''), 10))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= factCount)
    .map((n) => n - 1);
  const uniqueIndexes = [...new Set(indexes)].slice(0, 2);

  const connective = CONNECTIVES.find((c) => connectiveLine.includes(c)) || null;

  return { indexes: uniqueIndexes, connective };
}

function buildBlurb(facts, selection) {
  const indexes = selection.indexes.length > 0 ? selection.indexes : facts.map((_, i) => i).slice(0, 2);
  const chosen = indexes.map((i) => facts[i]).filter(Boolean);
  if (chosen.length === 0) return null;
  if (chosen.length === 1) return chosen[0] + '.';
  const connective = selection.connective || 'and';
  return `${chosen[0]}, ${connective} ${chosen[1].charAt(0).toLowerCase()}${chosen[1].slice(1)}.`;
}

/**
 * @param {string[]} facts  pre-built, fully-trustworthy fact sentences (from
 *   formatFacts() in ScoutSuggestionPanel.js) — e.g. ["Gold at 5:00: 1237,
 *   XP: 1001", "First teamfight at 2:51 (3 deaths) — won"]
 * @returns {Promise<string|null>} a short blurb built ONLY from the given
 *   facts, or null if Ollama is unreachable/times out (never throws).
 */
export async function phraseWhy(facts) {
  if (!facts || facts.length === 0) return null;
  if (facts.length === 1) return facts[0] + '.'; // nothing to select between

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${OLLAMA_URL}/api/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        model: MODEL,
        prompt: selectionPrompt(facts),
        stream: false,
        keep_alive: '30m',
        options: { temperature: 0.1, num_predict: 20 },
      }),
    });
    if (!res.ok) return buildBlurb(facts, { indexes: [], connective: null });
    const data = await res.json();
    const selection = parseSelection((data && data.response) || '', facts.length);
    return buildBlurb(facts, selection);
  } catch {
    // Unreachable/timed out — still return a deterministic blurb from the
    // facts alone rather than nothing; this never invents content.
    return buildBlurb(facts, { indexes: [], connective: null });
  } finally {
    clearTimeout(timer);
  }
}
